import React, { useEffect, useRef, useState } from 'react';
import { AlertBanner } from '@spark';
import MissingLabelsFixMenu, { MISSING_LABELS_WALKTHROUGH_STEP } from './MissingLabelsFixMenu';
import { labelCatalogBannerText } from './labelMappingWarnings';

const signatureOf = (missing, unknown) => `${missing.join('\u0000')}\u0001${unknown.join('\u0000')}`;

/**
 * One catalog-mismatch warning for several uploaded files of a kind (all
 * datasets, or all benchmarks), rendered under the last file's details:
 * task labels/entities no file covers and file values the task does not
 * define. Same banner, Fix menu and dismissal rules as the per-file one in
 * `DatasetDetailsForm` — dismissed until either set changes.
 *
 * `onVisibilityChange` mirrors `DatasetDetailsForm.onMissingBannerChange`
 * (`true` / `false`, `null` on unmount) for the guided walkthrough.
 */
const CatalogMismatchBanner = ({
    missingLabels = [],
    unknownLabels = [],
    isNer = false,
    isExistingTask = false,
    onAddTaskLabels,
    onGenerateNewData,
    onAddMoreFiles,
    onVisibilityChange,
}) => {
    const currentSignature = signatureOf(missingLabels, unknownLabels);
    const [dismissedSignature, setDismissedSignature] = useState(null);
    const visible = (missingLabels.length > 0 || unknownLabels.length > 0) && dismissedSignature !== currentSignature;

    const onVisibilityChangeRef = useRef(onVisibilityChange);
    onVisibilityChangeRef.current = onVisibilityChange;
    useEffect(() => {
        onVisibilityChangeRef.current?.(visible);
    }, [visible]);
    useEffect(() => () => onVisibilityChangeRef.current?.(null), []);

    if (!visible) return null;

    const handleAddTaskLabels = () => {
        onAddTaskLabels?.(unknownLabels);
        // On an existing task the catalog is add-only, so labels covered by
        // the task's other files stay "missing" from these files forever —
        // clicking the fix acknowledges them. The wizard prunes them instead.
        if (isExistingTask) setDismissedSignature(signatureOf(missingLabels, []));
    };

    return (
        <div className="cw-dc-merged-banner" data-walkthrough={MISSING_LABELS_WALKTHROUGH_STEP}>
            <AlertBanner
                feedback="warning"
                showButton={false}
                showCloseButton
                text={labelCatalogBannerText({ missingLabels, unknownLabels, isNer })}
                action={
                    <MissingLabelsFixMenu
                        isNer={isNer}
                        isExistingTask={isExistingTask}
                        onAddTaskLabels={handleAddTaskLabels}
                        onGenerateNewData={onGenerateNewData}
                        onAddMoreFiles={onAddMoreFiles}
                    />
                }
                onCloseClick={() => setDismissedSignature(currentSignature)}
            />
        </div>
    );
};

export default CatalogMismatchBanner;
